import { useEffect, useState } from "react";

import api from "@services/api";
import { useToast } from "@hooks/useToast";
import useTranslation from "src/translations/useTranslation";
import { ToastMessages } from "@shared/languages/interfaces";
import { TravelCycleSummary } from "@shared/interfaces/travel-cycle-analysis.interface";

import { TravelAnalysisFilter } from "../types";

/** User's UTC offset in hours, so the API splits the period by the user's days. */
export const userUtcOffset = (): number => -new Date().getTimezoneOffset() / 60;

/** Return shape of the `useTravelCycleAnalysis` hook. */
interface UseTravelCycleAnalysisReturn {
  summary: TravelCycleSummary | null;
  loading: boolean;
}

/**
 * Loads the production cycle summary (KPIs, causes, regional/plant breakdown, hardware to check)
 * every time the screen filter changes.
 * @param filter Current filter of the screen; nothing is loaded while it is null.
 */
const useTravelCycleAnalysis = (filter: TravelAnalysisFilter | null): UseTravelCycleAnalysisReturn => {

  const { addToast } = useToast();
  const { t } = useTranslation();
  const [summary, setSummary] = useState<TravelCycleSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!filter) return undefined;

    let active = true;

    setLoading(true);
    api.post("travels/analysis/cycle/summary", { ...filter, utcOffset: userUtcOffset() })
      .then(({ data }) => {
        if (!active) return;
        if (data.status === "success") setSummary(data.result);
        else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      })
      .catch((err) => {
        if (!active) return;
        addToast({
          type: "error",
          title: t(ToastMessages.error),
          description: err?.response?.data?.message || t(ToastMessages.connectionNotEstablished)
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return { summary, loading };
};

export default useTravelCycleAnalysis;
